import { createGlobalStyle } from "styled-components";
import { extendTheme } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";

export const colorTokens = {
  bg: {
    app: "#101826",
    surface: "#162236",
    surfaceElevated: "#1C2B44",
    surfaceMuted: "#243552",
    hover: "#2D4367",
    overlay: "#000000",
    sheetLight: "#FFFFFF",
  },
  text: {
    primary: "#FFFFFF",
    secondary: "#E0E0E0",
    muted: "#A8A8A8",
    inverse: "#111111",
    dimmed: "#888888",
    placeholder: "#9E9E9E",
    title: "#FCCB06",
    legend: "#FCCB06",
  },
  border: {
    default: "#35507A",
    strong: "#446293",
  },
  accent: {
    primary: "#5FA8FF",
  },
  status: {
    success: "#008000",
    successHover: "#006600",
    danger: "#E86A6A",
    dangerHover: "#D45A5A",
  },
} as const;

export const textTokens = {
  fontFamily: '"Public Sans", sans-serif',
  color: {
    primary: colorTokens.text.primary,
    secondary: colorTokens.text.secondary,
    muted: colorTokens.text.muted,
    inverse: colorTokens.text.inverse,
    dimmed: colorTokens.text.dimmed,
    placeholder: colorTokens.text.placeholder,
    title: colorTokens.text.title,
    legend: colorTokens.text.legend,
    bodyOnDark: colorTokens.text.primary,
    bodyOnLight: colorTokens.text.inverse,
  },
} as const;

export const ctaAccentColor = colorTokens.text.title;
export const ctaAccentHoverColor = "#E5B800";
export const ctaAccentTextColor = "#1A1A1A";
export const ctaSuccessColor = colorTokens.status.success;
export const ctaSuccessHoverColor = colorTokens.status.successHover;
export const ctaDangerColor = colorTokens.status.danger;
export const ctaDangerHoverColor = colorTokens.status.dangerHover;

const ctaDisabledGray = colorTokens.text.muted;

export function withAlpha(hexColor: string, alpha: number): string {
  const normalized = hexColor.replace("#", "");
  if (normalized.length !== 6) return `rgba(255,255,255,${alpha})`;

  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export const hrCardSx: SxProps = {
  backgroundColor: colorTokens.bg.surface,
  color: colorTokens.text.primary,
};

export const roundActionIconButtonSx: SxProps = {
  width: 48,
  height: 48,
  borderRadius: "50%",
};

export const ctaIconButtonDisabledSx: SxProps = {
  "&:disabled, &[aria-disabled='true'], &.Mui-disabled": {
    color: `${ctaDisabledGray} !important`,
    "--Icon-color": `${ctaDisabledGray} !important`,
    backgroundColor: "transparent !important",
    opacity: 1,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "transparent !important",
      color: `${ctaDisabledGray} !important`,
      "--Icon-color": `${ctaDisabledGray} !important`,
    },
    "& .MuiSvgIcon-root": {
      color: `${ctaDisabledGray} !important`,
    },
  },
};

export const ctaAccentEnabledSx: SxProps = {
  color: `${ctaAccentColor} !important`,
  "--Icon-color": `${ctaAccentColor} !important`,
  backgroundColor: "transparent",
  "& .MuiSvgIcon-root": {
    color: `${ctaAccentColor} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: withAlpha(ctaAccentColor, 0.15),
    color: `${ctaAccentHoverColor} !important`,
    "--Icon-color": `${ctaAccentHoverColor} !important`,
    "& .MuiSvgIcon-root": {
      color: `${ctaAccentHoverColor} !important`,
    },
  },
};

export const ctaSuccessEnabledSx: SxProps = {
  color: `${ctaSuccessColor} !important`,
  "--Icon-color": `${ctaSuccessColor} !important`,
  backgroundColor: "transparent",
  "& .MuiSvgIcon-root": {
    color: `${ctaSuccessColor} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: withAlpha(ctaSuccessColor, 0.2),
    color: `${ctaSuccessHoverColor} !important`,
    "--Icon-color": `${ctaSuccessHoverColor} !important`,
    "& .MuiSvgIcon-root": {
      color: `${ctaSuccessHoverColor} !important`,
    },
  },
};

export const ctaDangerEnabledSx: SxProps = {
  color: `${ctaDangerColor} !important`,
  "--Icon-color": `${ctaDangerColor} !important`,
  backgroundColor: "transparent",
  "& .MuiSvgIcon-root": {
    color: `${ctaDangerColor} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: withAlpha(ctaDangerColor, 0.2),
    color: `${ctaDangerHoverColor} !important`,
    "--Icon-color": `${ctaDangerHoverColor} !important`,
    "& .MuiSvgIcon-root": {
      color: `${ctaDangerHoverColor} !important`,
    },
  },
};

export const ctaAccentSx: SxProps = [ctaAccentEnabledSx, ctaIconButtonDisabledSx];
export const ctaSuccessSx: SxProps = [ctaSuccessEnabledSx, ctaIconButtonDisabledSx];
export const ctaDangerSx: SxProps = [ctaDangerEnabledSx, ctaIconButtonDisabledSx];

export type CtaVariant = "accent" | "success" | "danger" | "neutral";
export type JoyIconButtonSemanticColor = "danger" | "success" | "neutral";

export const joyIconButtonColorByCtaVariant: Record<CtaVariant, JoyIconButtonSemanticColor> = {
  danger: "danger",
  success: "success",
  accent: "neutral",
  neutral: "neutral",
};

export const ctaVariantEnabledSxMap: Record<CtaVariant, SxProps> = {
  accent: ctaAccentEnabledSx,
  success: ctaSuccessEnabledSx,
  danger: ctaDangerEnabledSx,
  neutral: ctaAccentEnabledSx,
};

/** Login / reset cards (`background.body` = app): autofill matches card, no white slab. Includes vertical spacing. */
export const authCardDarkInputSx: SxProps = {
  marginY: 1,
  "& input:-webkit-autofill": {
    WebkitTextFillColor: `${colorTokens.text.primary} !important`,
    caretColor: colorTokens.text.primary,
    boxShadow: `0 0 0 1000px ${colorTokens.bg.app} inset`,
  },
  "& input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
    boxShadow: `0 0 0 1000px ${colorTokens.bg.app} inset`,
  },
};

export const portalDockedActionsBarSx: SxProps = {
  position: "fixed",
  bottom: {
    xs: "max(12px, env(safe-area-inset-bottom, 0px))",
    sm: "max(8px, env(safe-area-inset-bottom, 0px))",
  },
  right: {
    xs: "max(12px, env(safe-area-inset-right, 0px))",
    sm: "max(8px, env(safe-area-inset-right, 0px))",
  },
  width: { xs: "auto", md: "10%" },
  minWidth: { xs: "68px", sm: "84px" },
  height: "60px",
  px: { xs: 1, sm: 2 },
  backgroundColor: colorTokens.bg.surface,
  zIndex: 1000,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "10px",
  boxShadow: `0 4px 16px ${withAlpha(colorTokens.bg.overlay, 0.35)}`,
  touchAction: "manipulation",
  WebkitTapHighlightColor: "transparent",
};

export const GlobalStyles = createGlobalStyle`
  html,
  body {
    background-color: ${colorTokens.bg.app};
    color: ${textTokens.color.primary};
    font-family: ${textTokens.fontFamily};
    overflow: auto;
  }

  .MuiTypography-root,
  .MuiInputBase-input,
  .MuiButton-root,
  .MuiChip-label {
    color: inherit;
    font-family: inherit;
  }
`;

export const zekayTheme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        background: { body: colorTokens.bg.app },
        text: { primary: colorTokens.text.primary },
      },
    },
    dark: {
      palette: {
        background: { body: colorTokens.bg.app },
        text: { primary: colorTokens.text.primary },
      },
    },
  },
  typography: {
    h1: { color: textTokens.color.title, fontFamily: textTokens.fontFamily },
    h2: { color: textTokens.color.title, fontFamily: textTokens.fontFamily },
    h3: { color: textTokens.color.title, fontFamily: textTokens.fontFamily },
    h4: { color: textTokens.color.title, fontFamily: textTokens.fontFamily, fontSize: "0.95rem" },
    "title-lg": { color: textTokens.color.title, fontFamily: textTokens.fontFamily },
    "title-md": { color: textTokens.color.title, fontFamily: textTokens.fontFamily },
    "title-sm": { color: textTokens.color.title, fontFamily: textTokens.fontFamily },
    "body-lg": { color: textTokens.color.bodyOnDark, fontFamily: textTokens.fontFamily },
    "body-md": { color: textTokens.color.bodyOnDark, fontFamily: textTokens.fontFamily },
    "body-sm": { color: textTokens.color.secondary, fontFamily: textTokens.fontFamily },
    "body-xs": { color: textTokens.color.muted, fontFamily: textTokens.fontFamily },
  },
  fontFamily: {
    body: textTokens.fontFamily,
  },
});
